import React from 'react';
import { Box, TextField, IconButton, InputAdornment } from '@mui/material';
import { Search, Close, FilterList } from '@mui/icons-material';

/**
 * Reusable Search Box Component - Odoo style
 * Used across all list pages for search functionality
 * 
 * @param {string} value - Current search value
 * @param {function} onChange - Callback when search value changes
 * @param {string} placeholder - Placeholder text (default: "Search...")
 * @param {boolean} showFilter - Whether to show filter button (default: false)
 * @param {function} onFilterClick - Callback when filter button is clicked
 * @param {boolean} showClear - Whether to show clear button when there's value (default: true)
 * @param {function} onClear - Callback when clear button is clicked
 * @param {object} sx - Additional styles
 * @param {number} width - Width of search box (default: responsive)
 */
const SearchBox = ({
  value = '',
  onChange,
  placeholder = 'Search...',
  showFilter = false,
  onFilterClick,
  showClear = true,
  onClear,
  sx = {},
  width,
}) => {
  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #ccc',
        borderRadius: '4px',
        bgcolor: 'white',
        width: width || { xs: '100%', sm: 280, md: 320 },
        height: 34,
        '&:focus-within': {
          borderColor: '#875A7B',
          boxShadow: '0 0 0 2px rgba(135, 90, 123, 0.1)',
        },
        ...sx,
      }}
    >
      {/* Search Icon */}
      <Box
        sx={{
          px: 1,
          display: 'flex',
          alignItems: 'center',
          borderRight: '1px solid #ccc',
          height: '100%',
        }}
      >
        <Search sx={{ fontSize: 18, color: '#999' }} />
      </Box>

      {/* Search Input */}
      <TextField
        placeholder={placeholder}
        variant="standard"
        size="small"
        value={value}
        onChange={onChange}
        InputProps={{
          disableUnderline: true,
          sx: { fontSize: '14px', px: 1, py: 0 },
          endAdornment: (
            <InputAdornment position="end">
              {showClear && value && (
                <IconButton 
                  size="small" 
                  onClick={handleClear}
                  sx={{ p: 0.25, mr: 0.5 }}
                >
                  <Close sx={{ fontSize: 16, color: '#999' }} />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        sx={{ 
          flex: 1, 
          '& .MuiInputBase-root': { height: 32 } 
        }}
      />

      {/* Filter Button */}
      {showFilter && (
        <Box
          sx={{
            px: 0.5,
            display: 'flex',
            alignItems: 'center',
            borderLeft: '1px solid #ccc',
            height: '100%',
          }}
        >
          <IconButton 
            size="small" 
            onClick={onFilterClick}
            sx={{ p: 0.5 }}
          >
            <FilterList sx={{ fontSize: 18, color: '#666' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default SearchBox;
